/* Copyright Flexday Solutions LLC, Inc - All Rights Reserved

 * Unauthorized copying of this file, via any medium is strictly prohibited

 * Proprietary and confidential

 * See file LICENSE.txt for full license details.

 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import CustomToolbar from '../components/customToolbar';
import Header from '../../../components/form/header.component';
import PageContainer from '../../../components/pageContainer';
import { SECTIONS } from '../../../constants/drawer';
import { ACCESS_CONTROL } from '../../../constants/path';
import { GROUPS, ADD_PAGE } from '../../../constants/accessControlPaths';
import { NoDataTitle, TableStyled } from '../styled';
import {
  useDeleteManyRBACMutation,
  useLazyGetAllRBACQuery,
} from '../../../redux/services/speciphicAsk';
import { RBAC_GROUPS_ENDPOINT } from '../../../constants/accessControl';
import Loader from '../../../components/loader/loader.component';
import DisplayTime from '../../../components/timezone.component';
import { filterByIds, sortByDateField } from '../../../utils/functions';
import UnauthorizedMessage from '../../../components/unauthorized';
import { pushToast } from '../../../redux/reducers/toasts.slice';
import DialogComponent from '../../../components/app.dialog';

const addPageRoute = `${ACCESS_CONTROL}${GROUPS.endpoint}${ADD_PAGE.endpoint}`;

const translationJSONPrefix = `accessControlSection.groups.mainPage`;

const unauthorizedTranslationPrefix = `${translationJSONPrefix}.unauthorizedMessages`;

const columns = (t) => {
  const translationPrefix = `${translationJSONPrefix}.table.columnHeaders`;

  return [
    {
      field: 'name',
      headerName: t(`${translationPrefix}.name`),
      flex: 0.5,
      minWidth: 200,
      valueGetter: (params) => `${params.row?.name}`,
    },
    {
      field: 'role',
      headerName: t(`${translationPrefix}.role`),
      flex: 0.3,
      minWidth: 100,
      valueGetter: (params) => `${params.row?.roleName}`,
    },
    {
      field: 'policies',
      headerName: t(`${translationPrefix}.policies`),
      flex: 0.3,
      minWidth: 100,
      valueGetter: (params) => `${params.row?.policyCount}`,
    },
    {
      field: 'description',
      headerName: t(`${translationPrefix}.description`),
      flex: 0.5,
      minWidth: 200,
    },
    {
      field: 'createdBy',
      headerName: t(`${translationPrefix}.createdBy`),
      flex: 0.5,
      minWidth: 200,
    },
    {
      field: 'createdAt',
      headerName: t(`${translationPrefix}.createdAt`),
      flex: 0.5,
      minWidth: 200,
      renderCell: (params) => <DisplayTime time={params.row?.createdDate} />,
    },
    {
      field: 'updatedAt',
      headerName: t(`${translationPrefix}.updatedAt`),
      flex: 0.5,
      minWidth: 200,
      renderCell: (params) => (
        <DisplayTime time={params.row?.lastUpdatedDate} />
      ),
    },
  ];
};

const AccessControlGroupsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [selectedGroups, setSelectedGroups] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogTFValue, setDeleteDialogTFValue] = useState('');
  const [dialogDeleteButtonDisabled, setDialogDeleteButtonDisabled] =
    useState(true);

  const [getAllRBAC, { data, error, isError, isLoading, isSuccess }] =
    useLazyGetAllRBACQuery();

  const [
    deleteRBACs,
    {
      data: deletedResponse,
      error: deleteError,
      isError: isDeleteError,
      isLoading: isDeleteLoading,
      isSuccess: isDeleteSuccess,
    },
  ] = useDeleteManyRBACMutation();

  useEffect(() => {
    setDialogDeleteButtonDisabled(true);
    setDeleteDialogTFValue('');
  }, [deleteDialogOpen]);

  useEffect(() => {
    if (deleteError?.status === 403) {
      dispatch(
        pushToast({
          message: t(`${unauthorizedTranslationPrefix}.delete`),
          severity: 'error',
        }),
      );
    }
  }, [isDeleteError]);

  useEffect(() => {
    getAllRBAC({
      endpoint: RBAC_GROUPS_ENDPOINT,
    });

    setSelectedGroups([]);
  }, [deletedResponse]);

  const handleCellClick = (e) => {
    if (e?.field === 'name') navigate(`${addPageRoute}?id=${e?.id}`);
  };

  useEffect(() => {
    setDeleteDialogOpen(false);
  }, [isDeleteSuccess]);

  if (error?.status === 403)
    return (
      <UnauthorizedMessage
        drawerOpen={isDrawerOpen}
        drawer={SECTIONS.ACCESS_CONTROL}
        description={t(`${unauthorizedTranslationPrefix}.list`)}
      />
    );

  return (
    <PageContainer
      isDrawerOpen={(res) => setIsDrawerOpen(res)}
      drawer={SECTIONS.ACCESS_CONTROL}
    >
      <DialogComponent
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        setTextFieldValue={setDeleteDialogTFValue}
        textField={{
          name: 'name',
          value: deleteDialogTFValue,
          onChange: (e) => {
            setDeleteDialogTFValue(e.target.value);

            if (selectedGroups.length > 1) {
              if (e.target.value === 'permanently delete')
                setDialogDeleteButtonDisabled(false);
              return;
            }

            const p_names = filterByIds(selectedGroups, data).map(
              (p) => p.name,
            );

            setDialogDeleteButtonDisabled(!(e.target.value === p_names[0]));
          },
          placeholder:
            selectedGroups.length > 1
              ? t(
                  `accessControlSection.groups.deleteDialog.textFieldPlaceholderPlural`,
                )
              : t(
                  `accessControlSection.groups.deleteDialog.textFieldPlaceholder`,
                ),
        }}
        title={`${t(`accessControlSection.groups.deleteDialog.title`)}`}
        content={
          selectedGroups.length > 1
            ? t(`accessControlSection.groups.deleteDialog.contentPlural`)
            : `${t(`accessControlSection.groups.deleteDialog.content`)}`
        }
        highlightedTitle={
          selectedGroups.length === 1
            ? `${t(
                `accessControlSection.groups.deleteDialog.highlightedTitle`,
              )}`
            : ''
        }
        highlightedContent={`${
          selectedGroups.length === 1
            ? filterByIds(selectedGroups, data)[0]?.name
            : ''
        }`}
        onSubmit={(e) => {
          deleteRBACs({
            endpoint: RBAC_GROUPS_ENDPOINT,
            ids: selectedGroups,
          });
        }}
        submitBtnDisabled={dialogDeleteButtonDisabled}
        isDeleteLoading={isDeleteLoading}
        cancelBtnLabel={t(
          `accessControlSection.groups.deleteDialog.buttons.cancel`,
        )}
        submitBtnLabel={t(
          `accessControlSection.groups.deleteDialog.buttons.delete`,
        )}
      />
      <Header
        header={t(`${translationJSONPrefix}.title`)}
        addButtonLabel={t(`${translationJSONPrefix}.addButtonLabel`)}
        deleteButton={true}
        deleteButtonDisabled={selectedGroups.length === 0}
        addButtonOnClick={() => navigate(addPageRoute)}
        deleteButtonOnClick={() => setDeleteDialogOpen(true)}
        isDeleteLoading={isDeleteLoading}
      />
      {isLoading ? (
        <Loader label={t(`${translationJSONPrefix}.loadingLabel`)} />
      ) : data?.length > 0 ? (
        <TableStyled
          onCellClick={handleCellClick}
          rows={sortByDateField([...data], 'lastUpdatedDate')}
          columns={columns(t)}
          pageSize={10}
          checkboxSelection
          autoHeight
          onSelectionModelChange={setSelectedGroups}
          components={{
            Toolbar: CustomToolbar,
          }}
        />
      ) : (
        <NoDataTitle>{t(`${translationJSONPrefix}.noDataLabel`)}</NoDataTitle>
      )}
    </PageContainer>
  );
};

export default AccessControlGroupsPage;
